import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'Autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'legal@autopilot.co.zw';
const lastUpdated = '1 January 2025';

const sections = [
  {
    id: 'overview',
    title: '1. Overview',
    content: `${companyName} ("we", "us", "our") operates a chatbot automation platform accessible at autopilot.co.zw and related sub-domains ("the Platform"). This Privacy Policy explains how we collect, use, store, disclose, and protect personal information when you:

• Register for or use the Platform as a business operator ("Operator");
• Interact with a bot deployed by an Operator as an end-user ("End User"); or
• Visit our website.

By using the Platform, you confirm you have read and understood this Policy. If you do not agree, please stop using the Platform immediately.`,
  },
  {
    id: 'what-we-collect',
    title: '2. What Information We Collect',
    content: `**2.1 Information you give us directly (Operators)**
• Account registration: name, business name, email address, phone number, country.
• Payment information: billing name, payment method details (processed by our payment processor — we do not store raw card numbers).
• Business details submitted during bot setup: business address, Facebook Page details, WhatsApp phone numbers, website URLs, API credentials (Phone Number IDs, Access Tokens stored encrypted at rest).
• Communications: support tickets, emails, and chat messages you send us.

**2.2 Information collected automatically**
• Log data: IP address, browser type, pages visited, time and date of access, referring URL.
• Cookies and tracking: session cookies, analytics cookies, preference cookies. See our Cookie Policy for full details.
• Device information: device type, operating system, unique device identifiers.

**2.3 Information from third parties**
• Facebook / Meta: when you connect a Facebook or WhatsApp account, we receive account identifiers, page names, phone number IDs, and access tokens necessary to operate your bot.
• Payment processors (Stripe, EcoCash, OneMoney): transaction confirmations, partial card details (last 4 digits, brand, expiry), and customer identifiers.

**2.4 End User conversation data**
When End Users interact with a bot deployed by an Operator, we process the messages exchanged, including any personal details the End User chooses to share (e.g. name, phone number, booking details). We process this data on behalf of the Operator — the Operator is the data controller for their End Users' data.`,
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Information',
    content: `We use personal information for the following purposes, each grounded in a lawful basis:

**Service delivery (contractual necessity)**
• Creating and maintaining your account.
• Operating your bots and processing conversations.
• Processing payments and issuing invoices.
• Sending service-critical notifications (downtime, security alerts, invoice due dates).

**Compliance with legal obligations**
• Verifying business identity as required by Meta's WhatsApp Business API terms.
• Retaining financial records as required by Zimbabwean tax law.
• Responding to lawful requests from regulators or courts.

**Legitimate interests**
• Improving the Platform through aggregated, anonymised usage analytics.
• Fraud detection and security monitoring.
• Sending product updates and feature announcements (you may opt out at any time).

**Consent (where we ask)**
• Marketing emails beyond transactional notices.
• Non-essential analytics cookies.

We do not sell your personal information to third parties. We do not use conversation data from End Users to train AI models without explicit consent.`,
  },
  {
    id: 'facebook-whatsapp',
    title: '4. Facebook & WhatsApp Integration — Special Notice',
    content: `Because our Platform integrates deeply with Meta's systems, the following specific rules apply:

**4.1 Permissions we request from your Facebook account**
To operate a Facebook Messenger bot, we request the following permissions on your Page:
• pages_messaging — to send and receive Messenger messages on your behalf.
• pages_manage_metadata — to configure webhook subscriptions on your Page.
We request only the minimum permissions necessary. We do not request access to your personal Facebook profile, friends list, or advertising data.

**4.2 Admin access to your Facebook Page**
When you select our "We set it up for you" option for Facebook bots, our team will request to be added as a Page Manager or Developer on your Facebook Page for the duration of setup. You will receive a Facebook notification and must approve this request manually. We will remove our admin access once setup is complete, unless you request we retain it for ongoing support. You may remove our access at any time via Facebook Page Settings → Page Roles.

**4.3 WhatsApp Business API credentials**
Access Tokens and Phone Number IDs are stored encrypted (AES-256) in our database. They are used solely to send and receive WhatsApp messages on your behalf. These credentials are never shared with third parties other than those necessary to route messages (e.g. Meta's API endpoints).

**4.4 Meta's own policies**
Your use of WhatsApp Business API and Facebook Messenger is also governed by Meta's Platform Policies and Terms of Service. ${companyName} is not responsible for changes Meta makes to its policies. You are responsible for ensuring your use of our bots complies with Meta's Messaging Guidelines, including obtaining appropriate consent from End Users.

**4.5 Prohibited use**
You must not use our Platform to send spam, unsolicited commercial messages, content that violates Meta's Community Standards, or any content that is illegal under Zimbabwean or applicable international law.`,
  },
  {
    id: 'data-sharing',
    title: '5. Who We Share Your Data With',
    content: `We share personal information only in these circumstances:

**Service providers (processors acting on our instructions)**
• Cloud hosting: Firebase / Google Cloud Platform — servers located in the United States and Belgium.
• Payment processing: Stripe Inc. (cards), Cassava Fintech (EcoCash/OneMoney).
• Email delivery: SendGrid / similar transactional email provider.
• Analytics: anonymised, aggregated data only.

**Meta Platforms, Inc.**
We transmit your bot's messages and credentials to Meta's API endpoints to operate WhatsApp and Facebook bots. Meta processes this data under its own terms.

**Business transfers**
If ${companyName} is acquired or merges with another company, your data may be transferred as part of that transaction. We will notify you before this happens.

**Legal requirements**
We will disclose information if required by law, court order, or regulatory authority, or if we reasonably believe disclosure is necessary to protect the rights, property, or safety of ${companyName}, our users, or the public.

**With your consent**
Any other sharing only occurs with your explicit consent.`,
  },
  {
    id: 'data-retention',
    title: '6. Data Retention',
    content: `We retain personal information for as long as necessary to fulfil the purposes described in this Policy:

• **Account data**: Retained for the duration of your account plus 7 years after closure (to comply with Zimbabwean financial record-keeping requirements).
• **Conversation / bot flow data**: Retained for 24 months after the conversation, then anonymised or deleted.
• **Payment records**: Retained for 7 years as required by law.
• **Log and analytics data**: Retained for 12 months.
• **Support communications**: Retained for 3 years.

When we no longer need data, we delete it securely or anonymise it so it can no longer identify you.`,
  },
  {
    id: 'your-rights',
    title: '7. Your Rights',
    content: `Depending on your location, you may have the following rights regarding your personal information:

• **Access**: Request a copy of the personal data we hold about you.
• **Rectification**: Ask us to correct inaccurate or incomplete information.
• **Erasure**: Ask us to delete your personal data ("right to be forgotten"), subject to our legal retention obligations.
• **Restriction**: Ask us to limit how we process your data while a dispute is resolved.
• **Portability**: Receive your data in a structured, machine-readable format.
• **Objection**: Object to processing based on legitimate interests, including direct marketing.
• **Withdraw consent**: Where processing is based on consent, withdraw it at any time (this does not affect processing before withdrawal).

To exercise any of these rights, email us at ${companyEmail} with the subject line "Privacy Rights Request". We will respond within 30 days. We may need to verify your identity before fulfilling a request.

If you believe we have not handled your data correctly, you have the right to lodge a complaint with a relevant data protection authority.`,
  },
  {
    id: 'security',
    title: '8. Security',
    content: `We implement industry-standard technical and organisational measures to protect your information:

• All data in transit is encrypted using TLS 1.2 or higher.
• API credentials (Access Tokens, Phone Number IDs) are encrypted at rest using AES-256.
• Access to production systems is restricted to authorised personnel via multi-factor authentication.
• We conduct periodic security reviews and vulnerability assessments.
• Payment data is handled by PCI-DSS compliant processors — we do not store raw card numbers.

No system is 100% secure. In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will notify you and relevant authorities as required by applicable law, within 72 hours of becoming aware of it.`,
  },
  {
    id: 'international',
    title: '9. International Transfers',
    content: `${companyName} is based in Zimbabwe. Our infrastructure uses Google Cloud Platform, which may process your data in data centres located in the United States, Belgium, and other countries.

Where we transfer personal data outside Zimbabwe, we rely on:
• Standard Contractual Clauses approved by relevant data protection authorities;
• Adequacy decisions where applicable; or
• Your explicit consent.

By using the Platform, you acknowledge that your data may be processed internationally.`,
  },
  {
    id: 'children',
    title: '10. Children\'s Privacy',
    content: `The Platform is intended for business operators aged 18 and over. We do not knowingly collect personal information from children under 13. If we become aware that we have collected data from a child under 13 without verifiable parental consent, we will delete it promptly.

If you are a parent or guardian and believe your child has provided us with personal information, please contact us at ${companyEmail}.`,
  },
  {
    id: 'changes',
    title: '11. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. When we make material changes, we will:
• Update the "Last updated" date at the top of this page;
• Send a notification email to registered Operators; and
• Display a prominent notice on the Platform.

Your continued use of the Platform after the effective date of a revised Policy constitutes your acceptance of the changes.`,
  },
  {
    id: 'data-deletion',
    title: '12. Data Deletion',
    content: `You have the right to request deletion of your personal data at any time. This right covers all personal information ${companyName} holds about you, including account details, bot configurations, conversation history, and payment records (subject to legal retention obligations).

**How to request data deletion**
Send an email to ${companyEmail} with the subject line "Data Deletion Request" and include:
• Your full name and the email address associated with your account;
• The specific data you want deleted (or state "all data" for a full erasure request).

Alternatively, visit our dedicated data deletion page at https://autopilot.co.zw/data-deletion.

**What happens next**
• We will confirm receipt of your request within 3 business days.
• We will verify your identity before processing the request.
• Deletion will be completed within 30 days of verification.
• We will send you confirmation once deletion is complete.

**Data we may retain**
Some data may be retained beyond your deletion request where we have a legal obligation to do so:
• Financial records and invoices (7 years — Zimbabwean tax law);
• Fraud prevention and security records where required by law.

All retained data will be isolated from active systems and not used for any other purpose.

**Automated deletion**
When you delete your account via the dashboard, all bot flows, conversation data, and contact information are permanently deleted within 30 days. Payment records subject to legal retention are kept separately.`,
  },
  {
    id: 'contact',
    title: '13. Contact Us',
    content: `For any questions about this Privacy Policy or to exercise your rights, contact us:

**${companyName} — Privacy Team**
Email: ${companyEmail}
Subject line: "Privacy Policy Enquiry"

We aim to respond to all enquiries within 5 business days.`,
  },
];

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState('overview');

  const renderContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '14px', marginBottom: '8px', marginTop: i > 0 ? '20px' : 0 }}>{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.8', marginBottom: '8px' }}>
            {parts.map((part, j) => j % 2 === 1 ? <strong key={j} style={{ color: '#e2e8f0' }}>{part}</strong> : part)}
          </p>
        );
      }
      if (line.startsWith('• ')) {
        return <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.8', marginBottom: '6px', paddingLeft: '16px', borderLeft: '2px solid #1e2330' }}>{line.slice(2)}</p>;
      }
      if (line.trim() === '') return <div key={i} style={{ height: '8px' }} />;
      return <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.8', marginBottom: '8px' }}>{line}</p>;
    });
  };

  return (
    <>
      <SEO title={`Privacy Policy | ${companyName}`} description={`How ${companyName} collects, uses, and protects your personal information.`} keywords="privacy policy, data protection, GDPR" type="website" />

      <style>{`
        * { box-sizing: border-box; }
        .policy-nav-item { transition: all 0.15s; cursor: pointer; }
        .policy-nav-item:hover { background: rgba(91,79,232,0.1); }
        .policy-nav-item.active { background: rgba(91,79,232,0.15); border-left: 3px solid #5B4FE8; }
        @media (max-width: 768px) { .policy-layout { flex-direction: column !important; } .policy-sidebar { width: 100% !important; position: static !important; border-right: none !important; border-bottom: 1px solid #1e2330 !important; } }
      `}</style>

      <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: '#0f1117', color: 'white', minHeight: '100vh' }}>

        {/* Nav */}
        <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: '60px', background: 'rgba(15,17,23,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1e2330', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px' }}>
          <Link to="/"><img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} /></Link>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Terms</Link>
            <Link to="/cookies" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Cookies</Link>
            <Link to="/acceptable-use" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Acceptable Use</Link>
          </div>
        </nav>

        {/* Header */}
        <div style={{ paddingTop: '100px', paddingBottom: '40px', paddingLeft: '32px', paddingRight: '32px', borderBottom: '1px solid #1e2330', background: 'linear-gradient(135deg, #0f1117 0%, #12111a 100%)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ display: 'inline-block', background: 'rgba(91,79,232,0.15)', border: '1px solid rgba(91,79,232,0.3)', color: '#818cf8', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', letterSpacing: '0.08em', marginBottom: '16px' }}>LEGAL</div>
            <h1 style={{ fontSize: '36px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '8px' }}>Privacy Policy</h1>
            <p style={{ color: '#475569', fontSize: '14px' }}>Last updated: {lastUpdated}</p>
          </div>
        </div>

        {/* Layout */}
        <div className="policy-layout" style={{ display: 'flex', maxWidth: '1100px', margin: '0 auto', minHeight: 'calc(100vh - 200px)' }}>

          {/* Sidebar */}
          <div className="policy-sidebar" style={{ width: '260px', flexShrink: 0, borderRight: '1px solid #1e2330', padding: '24px 0', position: 'sticky', top: '60px', height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
            <div style={{ padding: '0 16px', marginBottom: '12px', fontSize: '10px', fontWeight: '700', color: '#334155', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Sections</div>
            {sections.map(s => (
              <div
                key={s.id}
                className={`policy-nav-item ${activeSection === s.id ? 'active' : ''}`}
                onClick={() => { setActiveSection(s.id); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                style={{ padding: '9px 20px', fontSize: '13px', color: activeSection === s.id ? '#a5b4fc' : '#64748b', borderLeft: activeSection === s.id ? '3px solid #5B4FE8' : '3px solid transparent', fontWeight: activeSection === s.id ? '600' : '400' }}
              >
                {s.title}
              </div>
            ))}
          </div>

          {/* Content */}
          <div style={{ flex: 1, padding: '40px 48px', overflowY: 'auto' }}>
            {sections.map(s => (
              <div key={s.id} id={s.id} style={{ marginBottom: '48px', scrollMarginTop: '80px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#e2e8f0', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #1e2330' }}>{s.title}</h2>
                {renderContent(s.content)}
              </div>
            ))}

            <div style={{ marginTop: '48px', padding: '24px', background: '#15181f', border: '1px solid #1e2330', borderRadius: '14px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '24px' }}>📧</span>
              <div>
                <div style={{ color: '#e2e8f0', fontWeight: '600', marginBottom: '4px' }}>Questions about this policy?</div>
                <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '10px' }}>Our privacy team is here to help.</div>
                <a href={`mailto:${companyEmail}`} style={{ color: '#818cf8', fontSize: '13px', textDecoration: 'none', fontWeight: '500' }}>{companyEmail}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
